/**
 * Date, number and list formatting in the current UI language
 * (backend RFC_I18N §5). Same named styles as the backend `src/i18n/format.js`:
 * patterns such as `DD.MM.YYYY` are banned, the language decides part order.
 */
import i18n from 'i18next';

export const DATE_STYLES = {
  short: { year: 'numeric', month: '2-digit', day: '2-digit' },
  medium: { year: 'numeric', month: 'short', day: 'numeric' },
  long: { year: 'numeric', month: 'long', day: 'numeric' },
  dayMonth: { month: 'long', day: 'numeric' },
  dayMonthShort: { month: 'short', day: 'numeric' },
  monthYear: { year: 'numeric', month: 'long' },
  month: { month: 'long' },
  weekday: { weekday: 'long' },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

export type DateStyle = keyof typeof DATE_STYLES;

export interface LocaleOption {
  /** Overrides the current UI language. */
  locale?: string;
}

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Requested UI language (`es-MX`, `ru`…), `en` before init. Not
 * `resolvedLanguage`: formatting and Accept-Language follow the regional
 * variant even when its texts come from the base language.
 */
export const currentLocale = (): string => i18n.language || i18n.resolvedLanguage || 'en';

const localeOf = (options?: LocaleOption) => options?.locale || currentLocale();

type DateInput = string | number | Date | null | undefined;

/**
 * A calendar date `YYYY-MM-DD` is formatted in UTC, so it never shifts by the
 * viewer's time zone. A timestamp (ISO with time, epoch ms, Date) is shown in
 * the viewer's local time.
 */
const toDate = (value: DateInput): { date: Date; calendar: boolean } | null => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return { date: value, calendar: false };
  if (typeof value === 'string' && CALENDAR_DATE.test(value)) {
    return { date: new Date(`${value}T00:00:00Z`), calendar: true };
  }
  return { date: new Date(value), calendar: false };
};

export const formatDate = (value: DateInput, style: DateStyle = 'medium', options: LocaleOption = {}): string => {
  const parsed = toDate(value);
  if (!parsed) return '';
  if (Number.isNaN(parsed.date.getTime())) return String(value);
  return new Intl.DateTimeFormat(localeOf(options), {
    ...(DATE_STYLES[style] || DATE_STYLES.medium),
    ...(parsed.calendar ? { timeZone: 'UTC' } : {}),
  }).format(parsed.date);
};

/** Date and time of a timestamp in the viewer's time zone. */
export const formatDateTime = (value: DateInput, style: DateStyle = 'medium', options: LocaleOption = {}): string => {
  const parsed = toDate(value);
  if (!parsed) return '';
  if (Number.isNaN(parsed.date.getTime())) return String(value);
  return new Intl.DateTimeFormat(localeOf(options), {
    ...(DATE_STYLES[style] || DATE_STYLES.medium),
    hour: '2-digit',
    minute: '2-digit',
    ...(parsed.calendar ? { timeZone: 'UTC' } : {}),
  }).format(parsed.date);
};

export const formatNumber = (
  value: number | string | null | undefined,
  { maximumFractionDigits = 2, minimumFractionDigits = 0, locale, ...rest }: Intl.NumberFormatOptions & LocaleOption = {},
): string => {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat(localeOf({ locale }), { maximumFractionDigits, minimumFractionDigits, ...rest }).format(n);
};

/** Month name (1–12) in the nominative form, for headings. */
export const monthName = (month: number, options: LocaleOption = {}): string =>
  new Intl.DateTimeFormat(localeOf(options), { month: 'long', timeZone: 'UTC' })
    .formatToParts(new Date(Date.UTC(2024, month - 1, 1)))
    .find((part) => part.type === 'month')?.value ?? String(month);

/** "a, b and c" by the rules of the language. */
export const formatList = (
  items: Array<string | number>,
  { type = 'conjunction', locale }: { type?: 'conjunction' | 'disjunction' | 'unit' } & LocaleOption = {},
): string => new Intl.ListFormat(localeOf({ locale }), { style: 'long', type }).format(items.map(String));

/**
 * dayjs pattern for antd DatePicker `format` in the current language, built from
 * the `short` style: `MM/DD/YYYY` (en), `DD.MM.YYYY` (ru), `DD/MM/YYYY` (es).
 */
export const datePickerFormat = (options: LocaleOption = {}): string => {
  const tokens: Record<string, string> = { year: 'YYYY', month: 'MM', day: 'DD' };
  return new Intl.DateTimeFormat(localeOf(options), { ...DATE_STYLES.short, timeZone: 'UTC' })
    .formatToParts(new Date(Date.UTC(2024, 10, 22)))
    .map((part) => tokens[part.type] ?? part.value)
    .join('');
};
